import { create, StateCreator } from 'zustand';
import { PersistOptions, persist } from 'zustand/middleware';

/**
 * Configuration for how to serialize/deserialize a specific field to/from URL hash
 */
export type FieldConfig<T = unknown> = {
    /** The URL parameter name */
    paramName: string;
    /** Serialize the value to a string (return undefined to skip serialization) */
    serialize: (value: T) => string | undefined;
    /** Deserialize the string back to the value (receives null if param doesn't exist) */
    deserialize: (value: string | null, defaultValue: T) => T;
};

/**
 * Configuration mapping state fields to their URL serialization rules
 */
export type HashStoreConfig<State> = {
    [K in keyof State]?: FieldConfig<State[K]>;
};

/**
 * Common serializers for typical data types
 */
export const serializers = {
    /** Serialize Date to YYYY-MM-DD format */
    date: {
        serialize: (date: Date | undefined): string | undefined =>
            date ? date.toISOString().split('T')[0] : undefined,
        deserialize: (value: string | null, defaultValue: Date): Date =>
            value ? new Date(value) : defaultValue,
    },
    /** Serialize optional Date to YYYY-MM-DD format */
    optionalDate: {
        serialize: (date: Date | undefined): string | undefined =>
            date ? date.toISOString().split('T')[0] : undefined,
        deserialize: (
            value: string | null,
            _defaultValue: Date | undefined,
        ): Date | undefined => (value ? new Date(value) : undefined),
    },
    /** Serialize optional string */
    optionalString: {
        serialize: (str: string | undefined): string | undefined => str,
        deserialize: (
            value: string | null,
            _defaultValue: string | undefined,
        ): string | undefined => value ?? undefined,
    },
    /** Serialize string array using pipe separator */
    stringArray: {
        serialize: (arr: string[]): string | undefined =>
            arr.length > 0 ? arr.join('|') : undefined,
        deserialize: (value: string | null, _defaultValue: string[]): string[] =>
            value ? value.split('|').filter(Boolean) : [],
    },
    /**
     * Serialize enum value with validation
     * @param validValues - Array of valid enum values
     * @returns Serializer for the enum type
     * @example
     * enum Status { Active = 'active', Inactive = 'inactive' }
     * serializers.enum(['active', 'inactive'])
     */
    enum: <T extends string | number>(validValues: readonly T[]) => ({
        serialize: (value: T): string | undefined => {
            if (validValues.includes(value)) {
                return String(value);
            }
            return undefined;
        },
        deserialize: (value: string | null, defaultValue: T): T => {
            if (value !== null) {
                // For number enums, try to parse as number
                const parsedValue =
                    typeof validValues[0] === 'number'
                        ? (Number(value) as T)
                        : (value as T);
                if (validValues.includes(parsedValue)) {
                    return parsedValue;
                }
            }
            return defaultValue;
        },
    }),
    /**
     * Serialize enum array using pipe separator with validation
     * @param validValues - Array of valid enum values
     * @returns Serializer for the enum array type
     * @example
     * enum Status { Active = 'active', Inactive = 'inactive' }
     * serializers.enumArray(['active', 'inactive'])
     */
    enumArray: <T extends string | number>(validValues: readonly T[]) => ({
        serialize: (arr: T[]): string | undefined => {
            const validItems = arr.filter((item) => validValues.includes(item));
            return validItems.length > 0 ? validItems.map(String).join('|') : undefined;
        },
        deserialize: (value: string | null, _defaultValue: T[]): T[] => {
            if (!value) {
                return [];
            }
            const items = value.split('|').filter(Boolean);
            // For number enums, try to parse as numbers
            const parsedItems =
                typeof validValues[0] === 'number'
                    ? items.map((item) => Number(item) as T)
                    : (items as T[]);
            return parsedItems.filter((item) => validValues.includes(item));
        },
    }),
};

/**
 * Creates a Zustand store with URL hash persistence
 *
 * @param getDefaultState - Function that returns the default state
 * @param config - Field-level serialization configuration
 * @param storeCreator - Zustand store creator function
 * @param storeName - Name for the persisted store
 * @returns Zustand store hook
 */
export function createHashStore<State, Actions>(
    getDefaultState: () => State,
    config: HashStoreConfig<State>,
    storeCreator: StateCreator<State & Actions, [['zustand/persist', unknown]]>,
    storeName: string,
) {
    // Custom storage implementation that uses URL hash
    const hashStorage = {
        getItem: (_name: string): { state: State; version?: number } | null => {
            try {
                const state = deserializeFromHash();
                return { state };
            } catch {
                return null;
            }
        },
        setItem: (_name: string, value: { state: State; version?: number }): void => {
            try {
                const state = value.state;
                const params = new URLSearchParams();

                // Serialize each configured field
                for (const [fieldName, fieldConfig] of Object.entries(config) as [
                    keyof State,
                    FieldConfig,
                ][]) {
                    const fieldValue = state[fieldName];
                    const serialized = fieldConfig.serialize(fieldValue);

                    if (serialized !== undefined) {
                        params.set(fieldConfig.paramName, serialized);
                    }
                }

                const newHash = params.toString();
                const newUrl = `${window.location.pathname}${window.location.search}${
                    newHash ? `#${newHash}` : ''
                }`;
                window.history.replaceState(null, '', newUrl);
            } catch {
                // Silently fail
            }
        },
        removeItem: (_name: string): void => {
            try {
                const hash = window.location.hash.slice(1);
                const params = new URLSearchParams(hash);

                // Remove all configured params
                for (const fieldConfig of Object.values(config) as FieldConfig[]) {
                    params.delete(fieldConfig.paramName);
                }

                const newHash = params.toString();
                const newUrl = `${window.location.pathname}${window.location.search}${
                    newHash ? `#${newHash}` : ''
                }`;
                window.history.replaceState(null, '', newUrl);
            } catch {
                // Silently fail
            }
        },
    };

    // Custom deserializer to parse from hash params
    const deserializeFromHash = (): State => {
        try {
            const hash = window.location.hash.slice(1);
            const params = new URLSearchParams(hash);
            const defaults = getDefaultState();

            const result: Partial<State> = {};

            // Deserialize each configured field
            for (const [fieldName, fieldConfig] of Object.entries(config) as [
                keyof State,
                FieldConfig<State[keyof State]>,
            ][]) {
                const defaultValue = defaults[fieldName];
                const paramValue = params.get(fieldConfig.paramName);

                result[fieldName] = fieldConfig.deserialize(paramValue, defaultValue);
            }

            return { ...defaults, ...result };
        } catch {
            return getDefaultState();
        }
    };

    // Create the store with persist middleware
    return create<State & Actions>()(
        persist(storeCreator, {
            name: storeName,
            storage: {
                getItem: hashStorage.getItem,
                setItem: hashStorage.setItem,
                removeItem: hashStorage.removeItem,
            },
        } as PersistOptions<State & Actions>),
    );
}
